import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { Link } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type MemberRole,
  type MemberSummary,
} from '../api/client';
import { isApiError } from '../api/errors';
import { toDisplayError, toFieldErrors, type DisplayError } from '../api/errorMessages';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { HelpTip } from '../components/HelpTip';
import { FormField } from '../components/FormField';
import { TextInput } from '../components/TextInput';
import { SelectField } from '../components/SelectField';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { ROLE_LABELS, ROLE_ORDER } from '../org/roleLabels';
import { MemberRow } from './MemberRow';
import { AuthedBrand } from './AuthedBrand';
import styles from './Members.module.css';

const ROLE_OPTIONS = ROLE_ORDER.map((role) => ({ value: role, label: ROLE_LABELS[role] }));

type LoadState = 'loading' | 'error' | 'ready';

interface FormSummary {
  message: string;
  requestId?: string;
}

export interface MembersProps {
  client?: ApiClient;
}

/**
 * Org members management (#64). Loads the roster (load-then-mutate like
 * RetentionSettings), lets an admin add a member by email and change/remove
 * per-row roles. Row-scoped results announce in each row's polite live region;
 * the add form and any 403 use ONE top-level assertive alert that takes focus.
 */
export function Members({ client = defaultClient }: MembersProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  useFocusMain();

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [members, setMembers] = useState<MemberSummary[]>([]);

  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState<MemberRole>('viewer');
  const [addEmailError, setAddEmailError] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);

  const [summary, setSummary] = useState<FormSummary | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const summaryRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const removeRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  // undefined = no pending focus; null = focus the add-email field; string = userId.
  const pendingFocus = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  const load = useCallback(async () => {
    if (!orgId) return;
    setPhase('loading');
    try {
      setMembers(await client.listMembers(orgId));
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  // After a removal, move focus to a deterministic target now that the row is gone.
  useEffect(() => {
    if (pendingFocus.current === undefined) return;
    const target = pendingFocus.current;
    pendingFocus.current = undefined;
    if (target === null) {
      emailRef.current?.focus();
      return;
    }
    const el = removeRefs.current.get(target);
    (el ?? emailRef.current)?.focus();
  }, [members]);

  const registerRemoveRef = useCallback((userId: string, el: HTMLButtonElement | null) => {
    if (el) removeRefs.current.set(userId, el);
    else removeRefs.current.delete(userId);
  }, []);

  const showForbidden = useCallback((display: DisplayError) => {
    setSummary({
      message: display.message,
      ...(display.requestId ? { requestId: display.requestId } : {}),
    });
  }, []);

  const handleRoleUpdated = useCallback((updated: MemberSummary) => {
    setMembers((prev) => prev.map((m) => (m.userId === updated.userId ? updated : m)));
  }, []);

  const handleRemoved = useCallback((userId: string) => {
    setMembers((prev) => {
      const idx = prev.findIndex((m) => m.userId === userId);
      if (idx === -1) return prev;
      const removed = prev[idx];
      const nextId = prev[idx + 1]?.userId ?? prev[idx - 1]?.userId ?? null;
      pendingFocus.current = nextId;
      setStatusMessage(`Removed ${removed?.email ?? removed?.userId ?? 'member'}.`);
      return prev.filter((m) => m.userId !== userId);
    });
  }, []);

  const submitAdd = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (addBusy || !orgId) return;

    const email = addEmail.trim();
    if (!email) {
      setAddEmailError('Enter an email address.');
      return;
    }

    setAddEmailError(null);
    setSummary(null);
    setAddBusy(true);
    try {
      const created = await client.addMember(orgId, { email, role: addRole });
      setMembers((prev) => [created, ...prev]);
      setAddEmail('');
      setStatusMessage(`Added ${created.email ?? email}.`);
      requestAnimationFrame(() => emailRef.current?.focus());
    } catch (err) {
      const fields = toFieldErrors(err);
      if (fields.email) {
        setAddEmailError(fields.email);
      } else if (isApiError(err) && err.status === 409) {
        setSummary({ message: 'That person is already a member of this organization.' });
      } else {
        // 429 (too_many_requests), 403 (forbidden), 5xx, network → top-level alert.
        const display = toDisplayError(err);
        setSummary({
          message: display.message,
          ...(display.requestId ? { requestId: display.requestId } : {}),
        });
      }
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 className={styles.heading}>Members</h1>
        <p className={styles.subheading}>Manage who can access this organization and their roles.{' '}
          <HelpTip term="membersRoles" />
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading members…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' ? (
          <>
            <div className={styles.summary}>
              <InlineAlert
                ref={summaryRef}
                severity="error"
                {...(summary?.requestId ? { requestId: summary.requestId } : {})}
              >
                {summary?.message}
              </InlineAlert>
            </div>

            <InlineAlert severity="success">{statusMessage}</InlineAlert>

            <section className={styles.section} aria-labelledby="add-member-heading">
              <h2 id="add-member-heading" className={styles.sectionHeading}>
                Add a member
              </h2>
              <form className={styles.addForm} onSubmit={submitAdd} noValidate>
                <div className={styles.addEmail}>
                  <FormField
                    label="Email"
                    id="member-email"
                    helpText="They must already have a TestCraft account."
                    {...(addEmailError ? { error: addEmailError } : {})}
                  >
                    {({ controlProps }) => (
                      <TextInput
                        ref={emailRef}
                        {...controlProps}
                        type="email"
                        name="member-email"
                        autoComplete="email"
                        value={addEmail}
                        onChange={(e) => setAddEmail(e.target.value)}
                      />
                    )}
                  </FormField>
                </div>
                <div className={styles.addRole}>
                  <SelectField
                    label="Role"
                    id="member-role"
                    value={addRole}
                    onChange={(v) => setAddRole(v as MemberRole)}
                    options={ROLE_OPTIONS}
                  />
                </div>
                <div className={styles.addActions}>
                  <Button type="submit" busy={addBusy}>
                    {addBusy ? 'Adding…' : 'Add member'}
                  </Button>
                </div>
              </form>
            </section>

            <section className={styles.section} aria-labelledby="members-heading">
              <h2 id="members-heading" className={styles.sectionHeading}>
                Current members
              </h2>
              {members.length === 0 ? (
                <p className={styles.muted}>No members yet.</p>
              ) : (
                <ul className={styles.list}>
                  {members.map((member) => (
                    <MemberRow
                      key={member.userId}
                      client={client}
                      orgId={orgId ?? ''}
                      member={member}
                      onRoleUpdated={handleRoleUpdated}
                      onRemoved={handleRemoved}
                      onForbidden={showForbidden}
                      registerRemoveRef={registerRemoveRef}
                    />
                  ))}
                </ul>
              )}
            </section>
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}
